/**
 * DocsLayout — the arbi-docs shell: a lightweight TopBar (theme-aware wordmark +
 * theme toggle + sign-out) and a 4-item nav, wrapping the routed <Outlet />.
 * Deliberately minimal — no search, notifications, credit, distributor selector,
 * badges, realtime, or collapse (this is a thin read-only docs surface). Custom
 * Arbi UI only (UI_STANDARDS §13); semantic tokens throughout.
 */
import { type JSX } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { Sun, Moon, LogOut, BookOpen, Monitor, Megaphone, Mail } from 'lucide-react';
import clsx from 'clsx';
import { useApp } from '../../hooks/useApp';

const NAV = [
  { to: '/', label: 'Help docs', icon: BookOpen, end: true },
  { to: '/system-requirements', label: 'System Requirements', icon: Monitor, end: false },
  { to: '/release-notes', label: 'Release Notes', icon: Megaphone, end: false },
  { to: '/contact', label: 'Contact', icon: Mail, end: false },
];

export default function DocsLayout(): JSX.Element {
  const { theme, toggleTheme, signOut } = useApp();
  const wordmark = theme === 'light' ? '/arbi-wordmark-light.svg' : '/arbi-wordmark-dark.svg';

  return (
    <div className="h-screen bg-surface-0 flex flex-col">
      {/* TopBar */}
      <header className="flex items-center justify-between gap-3 px-4 h-[52px] bg-topbar border-b border-border flex-shrink-0">
        <div className="flex items-center gap-2">
          <img src={wordmark} alt="Arbi" className="h-7" />
          <span className="text-xs font-medium text-ink-muted">Docs</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
            className="p-2 rounded-md text-ink-secondary hover:text-ink-primary hover:bg-surface-2 transition-colors"
          >
            {theme === 'light' ? <Moon size={15} /> : <Sun size={15} />}
          </button>
          <button
            type="button"
            onClick={() => void signOut()}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-ink-secondary hover:text-ink-primary hover:bg-surface-2 transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Nav */}
        <nav className="w-52 flex-shrink-0 border-r border-border bg-surface-1 p-3 space-y-1 overflow-auto">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors',
                  isActive
                    ? 'bg-accent/15 text-accent font-medium'
                    : 'text-ink-secondary hover:text-ink-primary hover:bg-surface-2'
                )
              }
            >
              <item.icon size={15} className="flex-shrink-0" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Routed content (each page manages its own h-full scroll) */}
        <main className="flex-1 min-w-0 min-h-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
